<template>
  <div class="flex flex-col">
    <div class="flex justify-between">
      <div class="text-xs uppercase">
        {{ title }}
      </div>

      <div v-if="lastUpdate" class="text-xs text-muted-foreground updated-at">
        {{ $t('components.salesreport.updated_at', {
          date: lastUpdateFormatted
        }) }}
      </div>
    </div>

    <Transition name="fade">
      <div v-if="!loading" class="flex flex-col mt-3">
        <!-- Current amount — prominent -->
        <div class="flex items-end justify-between">
          <span class="text-[12px] text-muted-foreground">{{ $t('components.salesreport.current_income') }}</span>
          <span class="text-[17px] font-semibold tracking-tight text-foreground leading-none">{{ currentFormatted }}</span>
        </div>

        <!-- Evolution pill -->
        <div class="flex justify-end mt-2">
          <EvolutionIndicator
            v-if="(evolution || percentage) && current > 0 && previous > 0"
            :value="evolution"
            :formatted="percentage && !isNaN(percentage) ? percentageFormatted : '0 %'"
            />
        </div>

        <!-- Previous amount -->
        <div class="flex items-center justify-between mt-3 pt-3 evo-divider">
          <span class="text-[12px] text-muted-foreground">{{ $t('components.salesreport.previously') }}</span>
          <span class="text-[13px] font-semibold text-muted-foreground">{{ previousFormatted }}</span>
        </div>
      </div>

      <div v-else>
        <Loading />
      </div>
    </Transition>
  </div>
</template>
<script setup lang="ts">
import { computed } from 'vue';
import Loading from './Loading.vue';
import EvolutionIndicator from './ui/EvolutionIndicator.vue';
import { useUserManagerStore } from '../stores/userManagerStore';
import { formatCurrencyForCenter, getLanguageCode } from '../utils/Languages';

const props = withDefaults(defineProps<{
  center?: number,
  title?: string;
  current: number;
  previous: number;
  lastUpdate?: Date | null;
  loading?: boolean;
}>(), {
  title: '',
  current: 0,
  previous: 0,
  lastUpdate: null,
  loading: false
})

const userManagerStore = useUserManagerStore();

const evolution = computed<number>(() => props.current - props.previous || 0)


const percentage = computed<number>(() => {
  if (props.previous === 0) {
    return 0
  }

  return ((props.current - props.previous) / props.previous) * 100
})

const c = computed(() => {
  return userManagerStore.displayedCenters.find(c => c.id === props.center)
})

const percentageFormatted = computed<string>(() => {
  return new Intl.NumberFormat(getLanguageCode(c.value?.language), {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(percentage.value / 100)
})

const currentFormatted = computed<string>(() => {
  return formatCurrencyForCenter(props.current, c.value)
})

const previousFormatted = computed<string>(() => {
  return formatCurrencyForCenter(props.previous, c.value)
})

const lastUpdateFormatted = computed(() => {
  if (!props.lastUpdate) {
    return ''
  }

  return new Intl.DateTimeFormat(getLanguageCode(c.value?.language), {
    timeStyle: 'short'
  }).format(props.lastUpdate)
})

</script>

<style scoped>
.evo-divider {
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}
:is(.dark *).evo-divider {
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}
</style>